import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Statistic,
  Table,
  Button,
  Spin,
  Typography,
  Tag,
  Space,
  Input,
  Tabs,
  Card,
  Tooltip as AntTooltip,
} from "antd";
import {
  DollarCircleOutlined,
  HistoryOutlined,
  SyncOutlined,
  FileTextOutlined,
  PercentageOutlined,
  RiseOutlined,
  SearchOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import axios from "axios";
import BASEURL from "../API/Config";
import * as XLSX from "xlsx";

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const FinancialReports = () => {
  const [loading, setLoading] = useState(true);
  const [paymentCollection, setPaymentCollection] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [refundSummary, setRefundSummary] = useState([]);
  const [taxReport, setTaxReport] = useState([]);
  const [invoiceReport, setInvoiceReport] = useState([]);
  const [profitLoss, setProfitLoss] = useState([]);
  const [searchText, setSearchText] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const [paymentRes, pendingRes, refundRes, taxRes, invoiceRes, pnlRes] = await Promise.all([
        axios.get(`${BASEURL}/reports/payment-collection`, config),
        axios.get(`${BASEURL}/reports/pending-payments`, config),
        axios.get(`${BASEURL}/reports/refund-summary`, config),
        axios.get(`${BASEURL}/reports/tax-report`, config),
        axios.get(`${BASEURL}/reports/invoice-report`, config),
        axios.get(`${BASEURL}/reports/profit-loss`, config),
      ]);

      setPaymentCollection(paymentRes.data);
      setPendingPayments(pendingRes.data);
      setRefundSummary(refundRes.data);
      setTaxReport(taxRes.data);
      setInvoiceReport(invoiceRes.data);
      setProfitLoss(pnlRes.data);
    } catch (error) {
      console.error("Error fetching financial reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (data, fileName, type) => {
    let exportData = [];
    
    if (type === "pending") {
      exportData = data.map((item, index) => ({
        "S.No": index + 1,
        "Order ID": item.orderId,
        "Customer": `${item.user?.firstname || ""} ${item.user?.lastname || ""}`,
        "Phone": item.user?.phoneNumber || "",
        "Amount": `₹${item.totalPrice}`,
        "Status": item.paymentInfo?.paymentStatus,
        "Method": item.paymentInfo?.method,
        "Date": new Date(item.createdAt).toLocaleDateString(),
      }));
    } else if (type === "invoice") {
      exportData = data.map((item, index) => ({
        "S.No": index + 1,
        "Invoice No": item.orderId,
        "Date": new Date(item.createdAt).toLocaleDateString(),
        "Customer": `${item.user?.firstname || ""} ${item.user?.lastname || ""}`,
        "Total Amount": `₹${item.totalPrice}`,
        "Order Status": item.orderStatus,
        "Payment Status": item.paymentInfo?.paymentStatus,
      }));
    } else if (type === "tax") {
      exportData = data.map((item, index) => ({
        "S.No": index + 1,
        "Month": item._id,
        "Total Sales": `₹${item.totalSales.toFixed(2)}`,
        "GST Collected": `₹${item.totalGST.toFixed(2)}`,
      }));
    } else {
      exportData = data;
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  const pendingColumns = [
    { title: "S.No", key: "sno", width: 70, render: (_, __, index) => index + 1 },
    { title: "Order ID", dataIndex: "orderId", key: "orderId", render: (id) => <span style={{fontWeight: 600}}>{id}</span> },
    { 
      title: "Customer", 
      key: "customer",
      render: (_, record) => `${record.user?.firstname || "N/A"} ${record.user?.lastname || ""}` 
    },
    { title: "Amount", dataIndex: "totalPrice", key: "amount", render: (val) => `₹${val}` },
    { 
      title: "Payment Status", 
      dataIndex: ["paymentInfo", "paymentStatus"], 
      key: "status",
      render: (status) => (
        <Tag color={status === "Failed" ? "red" : "orange"}>
          {status ? status.toUpperCase() : "PENDING"}
        </Tag>
      )
    },
    { title: "Method", dataIndex: ["paymentInfo", "method"], key: "method" },
    { title: "Date", dataIndex: "createdAt", key: "date", render: (date) => new Date(date).toLocaleDateString() },
  ];

  const invoiceColumns = [
    { title: "S.No", key: "sno", width: 70, render: (_, __, index) => index + 1 },
    { title: "Invoice #", dataIndex: "orderId", key: "orderId", render: (id) => <span style={{fontWeight: 600}}>{id}</span> },
    { title: "Date", dataIndex: "createdAt", key: "date", render: (date) => new Date(date).toLocaleDateString() },
    { 
      title: "Customer", 
      key: "customer",
      render: (_, record) => record.user ? `${record.user.firstname} ${record.user.lastname}` : "N/A"
    },
    { title: "Total", dataIndex: "totalPrice", key: "total", render: (val) => `₹${val}` },
    { 
      title: "Status", 
      dataIndex: "orderStatus", 
      key: "orderStatus",
      render: (s) => <Tag color="blue">{s}</Tag>
    },
  ];

  const taxColumns = [
    { title: "S.No", key: "sno", width: 70, render: (_, __, index) => index + 1 },
    { title: "Month", dataIndex: "_id", key: "month" },
    { title: "Total Sales", dataIndex: "totalSales", key: "sales", render: (val) => `₹${val.toFixed(2)}` },
    { title: "GST Collected", dataIndex: "totalGST", key: "gst", render: (val) => `₹${val.toFixed(2)}` },
  ];

  // Filtering Logic
  const filteredInvoices = invoiceReport.filter(item => 
    item.orderId?.toLowerCase().includes(searchText.toLowerCase()) ||
    `${item.user?.firstname} ${item.user?.lastname}`.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredPending = pendingPayments.filter(item => 
    item.orderId?.toLowerCase().includes(searchText.toLowerCase()) ||
    `${item.user?.firstname} ${item.user?.lastname}`.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredRefunds = refundSummary.filter(item => 
    item.orderId?.toLowerCase().includes(searchText.toLowerCase()) ||
    `${item.user?.firstname} ${item.user?.lastname}`.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredTax = taxReport.filter(item => 
    item._id?.toLowerCase().includes(searchText.toLowerCase())
  );

  const totalRevenue = profitLoss.reduce((acc, curr) => acc + curr.revenue, 0);
  const totalCost = profitLoss.reduce((acc, curr) => acc + curr.cost, 0);
  const totalProfit = totalRevenue - totalCost;

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" tip="Generating Financial Reports..." />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <Text style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>Financial Management</Text>
          <Title level={2} style={{ margin: 0, fontFamily: "Outfit, sans-serif" }}>Financial Reports Dashboard</Title>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
          
        </Space>
      </div>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card" style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Statistic
              title="Total Revenue"
              value={totalRevenue}
              precision={2}
              prefix={<DollarCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#212121", fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card" style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Statistic
              title="Total Expenses"
              value={totalCost}
              precision={2}
              prefix={<HistoryOutlined style={{ color: "#f5222d" }} />}
              valueStyle={{ color: "#f5222d", fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card" style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Statistic
              title="Net Profit"
              value={totalProfit}
              precision={2}
              prefix={<RiseOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: totalProfit >= 0 ? "#1890ff" : "#f5222d", fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card" style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Statistic
              title="Pending Payments"
              value={pendingPayments.length}
              prefix={<ClockCircleOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14", fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Revenue vs Expenses (Monthly)" bordered={false} style={{ borderRadius: 12 }}>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={profitLoss}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1890ff" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" stroke="#1890ff" fillOpacity={1} fill="url(#colorRev)" name="Revenue" />
                  <Area type="monotone" dataKey="cost" stroke="#f5222d" fillOpacity={0.1} fill="#f5222d" name="Expenses" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Payment Collections" bordered={false} style={{ borderRadius: 12 }}>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={paymentCollection}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="_id.date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalAmount" fill="#52c41a" radius={[4, 4, 0, 0]} name="Amount" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Detailed Tables */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <Input
            placeholder="Search by Order ID or Customer..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 350, borderRadius: 8 }}
            allowClear
          />
          <Button 
            type="primary" 
            icon={<FileExcelOutlined />} 
            onClick={() => handleExport(invoiceReport, "Invoice_Report", "invoice")} 
            style={{ background: "#107c41", borderColor: "#107c41", borderRadius: 8 }}
          >
            Export Invoices
          </Button>
        </div>
        <Tabs defaultActiveKey="1">
          <TabPane tab={<span><ClockCircleOutlined />Pending Payments</span>} key="1">
            <Table 
              dataSource={filteredPending} 
              columns={pendingColumns} 
              rowKey="_id" 
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
            />
          </TabPane>
          <TabPane tab={<span><FileTextOutlined />Invoices</span>} key="2">
            <Table 
              dataSource={filteredInvoices} 
              columns={invoiceColumns} 
              rowKey="_id" 
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
            />
          </TabPane>
          <TabPane tab={<span><PercentageOutlined />Tax (GST) Report</span>} key="3">
            <Table 
              dataSource={filteredTax} 
              columns={taxColumns} 
              rowKey="_id" 
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
            />
          </TabPane>
          <TabPane tab={<span><SyncOutlined />Refunds</span>} key="4">
            <Table 
              dataSource={filteredRefunds} 
              columns={[
                ...pendingColumns.slice(0, 4),
                { 
                  title: "Refund Status", 
                  dataIndex: ["paymentInfo", "refundStatus"], 
                  render: (s) => <Tag color="purple">{s || "REFUNDED"}</Tag>
                },
                pendingColumns[6]
              ]} 
              rowKey="_id" 
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <style>{`
        .report-card:hover {
          transform: translateY(-2px);
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(0,0,0,0.1) !important;
        }
      `}</style>
    </div>
  );
};

export default FinancialReports;


